/**
 * The TV simulator.
 *
 * It runs the same resolver the Kotlin player runs, on the same manifest the
 * device will be handed, so what a manager sees here is what the store sees —
 * including the states nobody wants to be surprised by: a screen that is closed,
 * and a screen with nothing playable.
 *
 * Three things make it an honest simulation rather than a mood board:
 *
 *  - `resolve()` from @pbtv/shared decides what plays, re-run on the same
 *    RESOLVE_INTERVAL_SEC cadence the device uses, so a daypart boundary flips
 *    the preview at the moment it will flip the TV.
 *  - Item timing is the real timing: `durationSec` for stills, natural length x
 *    `repeatCount` for video.
 *  - Rotation is rendered the way the app renders it, by turning a canvas whose
 *    width and height are swapped inside the always-landscape framebuffer.
 *
 * What it does not promise is stated on the face of it: this is a browser
 * decoding these files, not a Fire TV stick. Timing, order, layout and rotation
 * are faithful. Exact colour, gamma and decode behaviour are not, and a video
 * that plays here is not by itself proof that a stick's decoder is happy with it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { RESOLVE_INTERVAL_SEC, resolve } from '@pbtv/shared';
import type {
  DeviceManifest,
  PlaybackSource,
  ResolvedItem,
  ResolvedState,
  RotationDegrees,
} from '@pbtv/shared';
import {
  DEFAULT_PREVIEW_RESOLUTION,
  canvasGeometry,
  cssObjectFit,
  fitLabel,
  rotationLabel,
} from './geometry.js';
import type { PreviewResolution } from './geometry.js';
import {
  advanceCursor,
  itemDurationMs,
  reconcileCursor,
  remainingMs,
  startCursor,
  tickCursor,
} from './timing.js';
import type { PlaybackCursor } from './timing.js';

/** Fast enough that a boundary never lands visibly late, cheap enough to ignore. */
const TICK_MS = 200;

const NO_ITEMS: readonly ResolvedItem[] = [];

export interface ScreenPreviewProps {
  manifest: DeviceManifest;
  /**
   * Overrides the manifest's own rotation, so the screen editor can preview a
   * change before it is saved.
   */
  rotationDegrees?: RotationDegrees;
  /** What the device last reported over HDMI. Fire TV always reports landscape. */
  resolution?: PreviewResolution;
  /**
   * How far the simulated clock sits from this browser's, in milliseconds. It is
   * an offset rather than a fixed instant so that scrubbing to 11:00 on a Tuesday
   * starts time there and lets it run: a boundary two minutes past the chosen
   * moment still arrives while somebody is watching for it. Zero is live.
   */
  clockOffsetMs?: number;
  /** Rendered under the status block, for what this particular preview is showing. */
  caption?: string;
}

export function ScreenPreview({
  manifest,
  rotationDegrees,
  resolution = DEFAULT_PREVIEW_RESOLUTION,
  clockOffsetMs = 0,
  caption,
}: ScreenPreviewProps): ReactElement {
  const offsetRef = useRef(clockOffsetMs);
  offsetRef.current = clockOffsetMs;

  const clockRef = useRef<() => number>(() => Date.now() + offsetRef.current);

  const rotation = rotationDegrees ?? manifest.screen.rotationDegrees;

  const [state, setState] = useState<ResolvedState>(() => resolve(manifest, clockRef.current()));
  const [cursor, setCursor] = useState<PlaybackCursor>(() => startCursor(clockRef.current()));
  const [nowMs, setNowMs] = useState<number>(() => clockRef.current());
  const [measuredMs, setMeasuredMs] = useState<number | null>(null);
  const [mediaNotice, setMediaNotice] = useState<string | null>(null);

  const items = state.kind === 'playing' ? state.items : NO_ITEMS;

  const itemsRef = useRef<readonly ResolvedItem[]>(items);
  const measuredRef = useRef<number | null>(measuredMs);
  const previousItemsRef = useRef<readonly ResolvedItem[]>(items);

  // Re-resolve on the device's cadence. The manifest object identity is the
  // trigger for an immediate re-run, which is what makes editing rotation or a
  // playlist feel instant; the offset is the other trigger, so scrubbing to
  // another hour answers straight away instead of on the next 15-second tick.
  useEffect(() => {
    const run = (): void => setState(resolve(manifest, clockRef.current()));
    run();
    const id = window.setInterval(run, RESOLVE_INTERVAL_SEC * 1000);
    return () => window.clearInterval(id);
  }, [manifest, clockOffsetMs]);

  // A jump in the clock restarts the loop rather than leaving the cursor holding
  // a start time measured on the old one. Landing at the top of the playlist is
  // also the useful answer to "what is on at 11:00" — you want to watch it from
  // the beginning, not from wherever the previous moment happened to be.
  useEffect(() => {
    setCursor(startCursor(clockRef.current()));
  }, [clockOffsetMs]);

  // A re-resolve usually returns an equivalent list; hold position when it does.
  useEffect(() => {
    setCursor((current) =>
      reconcileCursor(current, previousItemsRef.current, items, clockRef.current()),
    );
    previousItemsRef.current = items;
    itemsRef.current = items;
  }, [items]);

  // Whatever the browser reported belongs to the item that was on screen; a new
  // item starts unmeasured.
  useEffect(() => {
    setMeasuredMs(null);
    setMediaNotice(null);
  }, [cursor.index, cursor.startedAtMs]);

  useEffect(() => {
    measuredRef.current = measuredMs;
  }, [measuredMs]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const tick = clockRef.current();
      setNowMs(tick);
      setCursor((current) => tickCursor(current, itemsRef.current, tick, measuredRef.current));
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const current = items[cursor.index] ?? null;

  const advance = useCallback(() => {
    setCursor((currentCursor) => advanceCursor(currentCursor, itemsRef.current, clockRef.current()));
  }, []);

  const geometry = useMemo(() => canvasGeometry(resolution, rotation), [resolution, rotation]);

  const canvasStyle: CSSProperties = {
    width: `${geometry.widthPercent}%`,
    height: `${geometry.heightPercent}%`,
    transform: `translate(-50%, -50%) rotate(${geometry.rotationDegrees}deg)`,
    background: canvasBackground(state, current),
  };

  return (
    <div className="preview">
      <div className="preview-bezel">
        <div
          className="preview-frame"
          style={{ aspectRatio: `${resolution.width} / ${resolution.height}` }}
        >
          <div className="preview-canvas" style={canvasStyle}>
            <PreviewContent
              manifest={manifest}
              state={state}
              item={current}
              cursor={cursor}
              onEnded={advance}
              onMeasured={setMeasuredMs}
              onNotice={setMediaNotice}
            />
          </div>
        </div>
      </div>

      <PreviewStatus
        state={state}
        items={items}
        cursor={cursor}
        nowMs={nowMs}
        measuredMs={measuredMs}
        rotation={rotation}
        resolution={resolution}
        notice={mediaNotice}
      />

      {caption !== undefined ? <p className="preview-caption">{caption}</p> : null}

      <p className="preview-caveat">
        Faithful: what plays, in what order, for how long, at what size, turned which way. Not
        faithful: this is a browser decoding these files, not a Fire TV stick — colour, gamma and
        smoothness will differ, and a video playing here is not proof the stick&rsquo;s decoder is
        happy with it.
      </p>
    </div>
  );
}

function canvasBackground(state: ResolvedState, item: ResolvedItem | null): string {
  if (state.kind === 'closed') return '#000000';
  if (item === null) return '#0b0b0d';
  return item.backgroundColor;
}

interface PreviewContentProps {
  manifest: DeviceManifest;
  state: ResolvedState;
  item: ResolvedItem | null;
  cursor: PlaybackCursor;
  onEnded: () => void;
  onMeasured: (durationMs: number | null) => void;
  onNotice: (notice: string | null) => void;
}

function PreviewContent({
  manifest,
  state,
  item,
  cursor,
  onEnded,
  onMeasured,
  onNotice,
}: PreviewContentProps): ReactElement {
  if (state.kind === 'closed') {
    return <ClosedCard screenName={manifest.screen.name} />;
  }

  if (item === null) {
    const reason =
      state.kind === 'idle' && state.reason === 'nothing-playable'
        ? 'Waiting for content'
        : 'No content assigned';
    return <IdleCard screenName={manifest.screen.name} status={reason} />;
  }

  if (item.kind === 'video') {
    return (
      <PreviewVideo
        key={`${item.assetId}#${cursor.startedAtMs}`}
        item={item}
        onEnded={onEnded}
        onMeasured={onMeasured}
        onNotice={onNotice}
      />
    );
  }

  return (
    <img
      key={`${item.assetId}#${cursor.startedAtMs}`}
      className="preview-media"
      style={{ objectFit: cssObjectFit(item.fit) }}
      src={item.asset.url}
      alt=""
      draggable={false}
      onError={() => onNotice('This image could not be loaded from the CDN.')}
    />
  );
}

interface PreviewVideoProps {
  item: Extract<ResolvedItem, { kind: 'video' }>;
  onEnded: () => void;
  onMeasured: (durationMs: number | null) => void;
  onNotice: (notice: string | null) => void;
}

function PreviewVideo({ item, onEnded, onMeasured, onNotice }: PreviewVideoProps): ReactElement {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // With repeatCount > 1 the element loops and the timer does the advancing at
  // the total length; with a single play-through `ended` advances immediately,
  // which also covers a file whose real length differs from the probed one.
  const loops = item.repeatCount > 1;

  useEffect(() => {
    const element = videoRef.current;
    if (element === null) return;
    void element.play().catch(() => {
      onNotice('The browser blocked autoplay with sound. Click the frame to start it.');
    });
  }, [onNotice]);

  return (
    <video
      ref={videoRef}
      className="preview-media"
      style={{ objectFit: cssObjectFit(item.fit) }}
      src={item.asset.url}
      autoPlay
      playsInline
      muted={item.muted}
      loop={loops}
      onLoadedMetadata={(event) => {
        const seconds = event.currentTarget.duration;
        onMeasured(Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds * 1000) : null);
      }}
      onEnded={() => {
        if (!loops) onEnded();
      }}
      onError={() => onNotice('This video could not be loaded from the CDN.')}
      onClick={(event) => {
        void event.currentTarget.play().catch(() => undefined);
      }}
    />
  );
}

function ClosedCard({ screenName }: { screenName: string }): ReactElement {
  return (
    <div className="preview-state preview-state--closed">
      <span className="preview-state__label">Closed</span>
      <span className="preview-state__detail">{screenName || 'Screen'}</span>
    </div>
  );
}

function IdleCard({ screenName, status }: { screenName: string; status: string }): ReactElement {
  return (
    <div className="preview-state preview-state--idle">
      <span className="preview-brand" aria-hidden="true">
        PB
      </span>
      <span className="preview-state__label">{screenName || 'Screen'}</span>
      <span className="preview-state__detail">{status}</span>
    </div>
  );
}

interface PreviewStatusProps {
  state: ResolvedState;
  items: readonly ResolvedItem[];
  cursor: PlaybackCursor;
  nowMs: number;
  measuredMs: number | null;
  rotation: RotationDegrees;
  resolution: PreviewResolution;
  notice: string | null;
}

function PreviewStatus({
  state,
  items,
  cursor,
  nowMs,
  measuredMs,
  rotation,
  resolution,
  notice,
}: PreviewStatusProps): ReactElement {
  const item = items[cursor.index] ?? null;
  const remaining = remainingMs(cursor, items, nowMs, measuredMs);

  return (
    <div className="preview-status">
      <div className="preview-status__row">
        <span className={`chip chip--${stateTone(state)}`}>{stateLabel(state)}</span>
        <span className="preview-status__source">{sourceLabel(state)}</span>
        {items.length > 0 ? (
          <span className="preview-status__counter">
            Item {cursor.index + 1} of {items.length}
          </span>
        ) : null}
        {remaining !== null ? (
          <span className="preview-status__timer">{formatSeconds(remaining)} left</span>
        ) : null}
      </div>

      <dl className="preview-status__facts">
        <div>
          <dt>Rotation</dt>
          <dd>
            {rotation}&deg; — {rotationLabel(rotation)}
          </dd>
        </div>
        <div>
          <dt>Reported resolution</dt>
          <dd>
            {resolution.width} &times; {resolution.height}
          </dd>
        </div>
        {item !== null ? (
          <>
            <div>
              <dt>Fit</dt>
              <dd>{fitLabel(item.fit)}</dd>
            </div>
            <div>
              <dt>Letterbox</dt>
              <dd>
                <span
                  className="preview-swatch"
                  style={{ background: item.backgroundColor }}
                  aria-hidden="true"
                />
                {item.backgroundColor}
              </dd>
            </div>
            <div>
              <dt>Asset</dt>
              <dd className="mono">
                {item.assetId} · {item.asset.width}&times;{item.asset.height}
              </dd>
            </div>
            <div>
              <dt>On screen for</dt>
              <dd>{describeLength(item, measuredMs)}</dd>
            </div>
          </>
        ) : null}
      </dl>

      {notice !== null ? <p className="preview-status__notice">{notice}</p> : null}
    </div>
  );
}

function stateLabel(state: ResolvedState): string {
  switch (state.kind) {
    case 'closed':
      return 'Closed';
    case 'idle':
      return 'Idle';
    default:
      return 'Playing';
  }
}

function stateTone(state: ResolvedState): string {
  switch (state.kind) {
    case 'closed':
      return 'neutral';
    case 'idle':
      return 'warning';
    default:
      return 'success';
  }
}

function sourceLabel(state: ResolvedState): string {
  switch (state.kind) {
    case 'closed':
      return 'Outside the store’s opening hours';
    case 'idle':
      return state.reason === 'nothing-playable'
        ? 'Content is assigned but none of it can play'
        : 'Nothing assigned to this screen yet';
    default:
      return `${SOURCE_NAMES[state.source]}${state.playlistId === null ? '' : ` · ${state.playlistId}`}`;
  }
}

const SOURCE_NAMES: Record<PlaybackSource, string> = {
  override: 'Emergency override',
  schedule: 'Scheduled daypart',
  default: 'Default playlist',
  fallback: 'Fallback asset',
};

function describeLength(item: ResolvedItem, measuredMs: number | null): string {
  const total = itemDurationMs(item, measuredMs);
  if (item.kind === 'image') {
    return `${item.durationSec}s`;
  }
  if (total === null) return `${item.repeatCount}x, length unknown`;
  return `${item.repeatCount}x · ${formatSeconds(total)}`;
}

function formatSeconds(milliseconds: number): string {
  return `${(milliseconds / 1000).toFixed(1)}s`;
}
